import { useState } from 'react';

import { Employer, ResourcePages } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerTracksModalHeaderContent,
  CareerTracksModalBodyContent,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/CareerTracksModalContent';
import { handleBadgeClick } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/resource.lubav.util';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { TheScoop } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/TheScoop';
import {
  ResourcePageSection,
  ScoopStatItem,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectMakeItPersonalBadgesByType } from '@backrs_ui/grow-ui/src/redux/makeItPersonal/makeItPersonal';

import { useFetchOpportunitiesSectionData } from './hooks/useFetchOpportunitiesSectionData.hook';
import { useFetchPeopleSectionData } from './hooks/useFetchPeopleSectionData.hook';
import { useFetchResourcesSectionData } from './hooks/useFetchResourcesSectionData.hook';
import { CareerPathSection } from './molecules/CareerPathSection';
import { OpportunitiesSection } from './molecules/OpportunitiesSection';
import { PeopleSection } from './molecules/PeopleSection';
import { ResourceSection } from './molecules/ResourceSection';

const SCOOP_STRINGS = appStrings.modules.employer.scoop;

type EmployerPageProps = Readonly<{
  employer: Employer;
}>;

export const EmployerPage = ({ employer }: EmployerPageProps) => {
  const dispatch = useAppDispatch();

  const userId = useAppSelector((state) => state.user.user?.id);
  const { activeMakeItPersonalBadge } = useAppSelector((state) => state.makeItPersonal);

  const [opportunityFilters, setOpportunityFilters] = useState<SearchCategory[]>([]);
  const [resourceFilters, setResourceFilters] = useState<SearchCategory[]>([]);

  // filter the badges to only include the college badges
  const badges = useAppSelector(selectMakeItPersonalBadgesByType(ResourcePages.EMPLOYER));

  const summaryItems = [
    {
      title: SCOOP_STRINGS.about,
      description: employer.the_scoop_about || '',
    },
    {
      title: SCOOP_STRINGS.roles,
      description: employer.the_scoop_roles || '',
    },
  ];

  const statItems: ScoopStatItem[] = [
    {
      title: SCOOP_STRINGS.industry,
      iconName: 'Briefcase01',
      text: employer.industries[0]?.name || '',
    },
    {
      title: SCOOP_STRINGS.track.title,
      iconName: 'Route',
      text: employer?.minimum_track ? `${employer.minimum_track} or Higher` : '',
      infoModalProps: {
        headerContent: <CareerTracksModalHeaderContent />,
        bodyContent: <CareerTracksModalBodyContent />,
        icon: 'GitPullRequest',
      },
    },
    {
      title: SCOOP_STRINGS.size,
      iconName: 'Building08',
      text: employer.size || '',
    },
  ];

  const { opportunities, isLoading: opportunitiesLoading } = useFetchOpportunitiesSectionData(
    employer,
    opportunityFilters,
  );
  const { people, isLoading: peopleLoading } = useFetchPeopleSectionData(employer);
  const { resources, isLoading: resourcesLoading } = useFetchResourcesSectionData(
    employer,
    resourceFilters,
  );

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'the-scoop',
      title: 'The Scoop',
      component: <TheScoop summaryItems={summaryItems} statItems={statItems} />,
    },
    (opportunities.length > 0 || opportunityFilters.length > 0) && {
      id: 'opportunities',
      title: 'Opportunities',
      component: (
        <OpportunitiesSection
          opportunities={opportunities}
          isLoading={opportunitiesLoading}
          selectedFilters={opportunityFilters}
          onFilterChange={setOpportunityFilters}
        />
      ),
    },
    {
      id: 'career-path',
      title: 'Career Paths',
      component: <CareerPathSection employer={employer} />,
    },
    people.length > 0 && {
      id: 'people',
      title: 'People',
      component: <PeopleSection employer={employer} people={people} isLoading={peopleLoading} />,
    },
    (resources.length > 0 || resourceFilters.length > 0) && {
      id: 'resources',
      title: 'Resources',
      component: (
        <ResourceSection
          resources={resources}
          isLoading={resourcesLoading}
          selectedFilters={resourceFilters}
          onFilterChange={setResourceFilters}
        />
      ),
    },
  ].filter(Boolean) as ResourcePageSection[];

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          page={ResourcePages.EMPLOYER}
          title={employer.name}
          logo_media_url={employer.logo_media_url || '/images/employer-logo-default.svg'}
          city={employer.city}
          state={employer.region}
          country={employer.country}
          buttonMain={{
            label: 'Career Website',
            onClick: () => {
              window.open(employer.career_website, '_blank');
            },
          }}
          buttonSecondary={{
            label: `${employer.name} Website`,
            onClick: () => {
              window.open(employer.corporate_website, '_blank');
            },
          }}
        />
      }
      sections={PAGE_SECTIONS}
      chipLabel={employer.name}
      makeItPersonalProps={{
        pageName: ResourcePages.EMPLOYER,
        badges,
        activeBadge: activeMakeItPersonalBadge,
        onBadgeClick: (badge) =>
          handleBadgeClick({
            page: ResourcePages.EMPLOYER,
            badge,
            activeMakeItPersonalBadge,
            employer,
            userId,
            dispatch,
          }),
      }}
    />
  );
};
